import classnames from 'classnames'
import i18n from 'i18next'
import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs'
import PropTypes from 'prop-types'
import * as React from 'react'
import Draggable from 'react-draggable'
import EventListener from 'react-event-listener'
import Scrollspy from 'react-scrollspy'
import { useSwipeable } from 'react-swipeable'
// @mui
import { useMediaQuery } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import CssBaseline from '@mui/material/CssBaseline'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Hidden from '@mui/material/Hidden'
import Fab from '@mui/material/Fab'
import Slider from '@mui/material/Slider'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
// @mui icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
// import ZoomInIcon from '@mui/icons-material/AddCircleOutline'
// import ZoomOutIcon from '@mui/icons-material/RemoveCircleOutline'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CenterIcon from '@mui/icons-material/CenterFocusStrong'
import DownloadIcon from '@mui/icons-material/SaveAlt'
//
import ScrollTop from './common/ScrollTop'
import LayoutModeButton from './common/LayoutModeButton'
import FullscreenButton from './common/FullscreenButton'
import { useForceUpdateComponent } from './hooks/useForceUpdate'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    padding: 0,
    margin: 0,
    flexFlow: 'column',
    backgroundColor: theme.palette.background.viewer,

    '@media print': {
      '&:not(.printable)': {
        display: 'none !important'
      }
    },
  },
  body: {
    position: 'relative',
    overflow: 'auto',
    flex: '1 1 auto',
    //'-webkit-overflow-scrolling': 'touch',
  },
  bodyNoScroll: {
    overflow: 'hidden',
  },
  header: {
    '@media print': {
      '&': {
        display: 'none !important'
      }
    },
  },
  headerBar: {
    // [theme.breakpoints.up('md')]: {
    //   minHeight: 48,
    // },
  },
  footer: {
    '@media print': {
      '&': {
        display: 'none !important'
      }
    },
  },
  footerBar: {
    justifyContent: 'space-between',
  },
  footerBarLeft: {},
  footerBarCenter: {},
  footerBarRight: {},
  //offset: theme.mixins.toolbar,
  grow: {
    flexGrow: 1,
  },
  logo: {
    display: 'inline-block',
    // width: 24,
    height: 24,
    marginRight: theme.spacing(1),
  },
  title: {
    fontSize: theme.typography.pxToRem(14),
  },
  scaleSelectRoot: {
    color: theme.palette.primary.contrastText,
    fontSize: theme.typography.pxToRem(14),
    textAlign: 'left',
    whiteSpace: 'nowrap',

    [theme.breakpoints.up('sm')]: {
      minWidth: '8em',
      marginLeft: theme.spacing(1),
    },
  },
  navigations: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  pageSlider: {
    width: '5em',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',

    [theme.breakpoints.up('md')]: {
      width: '8em',
    },
  },
  canvasContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: 0,
    margin: 0,
  },
  canvasContainerSeq: {
    display: 'flex',
    height: 'auto',
    flexFlow: 'row nowrap',
    justifyItems: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  canvasAbsWrapper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    display: 'block',
    marginTop: theme.spacing(1),
    marginLeft: 0,
    marginRight: 0,
    marginBottom: theme.spacing(1),
    transform: 'translateX(-50%) translateY(-50%)',
    zIndex: 5,

    '&.in, &.out': {
      zIndex: 6,
    },

    '.react-draggable &': {
      cursor: 'move',
    },
  },
  canvasLeft: {
    transform: 'translateX(-100%) translateY(-50%)',
  },
  canvasRight: {
    transform: 'translateX(0px) translateY(-50%)',
  },
  canvas: {
    display: 'inline-block',
    border: '1px solid #999',
    boxSizing: 'border-box',
    transformStyle: 'preserve-3d',

    '$canvasRight.out &': {
      transformOrigin: 'left 0 0',
      animation: '$page-flip-out-right 150ms ease-in forwards',
    },
    '$canvasLeft.in &': {
      transformOrigin: 'right 0 0',
      animation: '$page-flip-in-left 300ms ease-out forwards',
    },
    '$canvasLeft.out &': {
      transformOrigin: 'right 0 0',
      animation: '$page-flip-out-left 150ms ease-in forwards',
    },
    '$canvasRight.in &': {
      transformOrigin: 'left 0 0',
      animation: '$page-flip-in-right 300ms ease-out forwards',
    },

    '.out &': {
      transformOrigin: 'left 0 0',
      animation: '$page-flip-out 150ms ease-in forwards',
    },
    '.in &': {
      transformOrigin: 'left 0 0',
      animation: '$page-flip-in 150ms ease-out forwards',
    },
  },
  textLayer: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    opacity: 0.2,
    lineHeight: 1.0,

    '& > span': {
      color: 'transparent',
      position: 'absolute',
      whiteSpace: 'pre',
      cursor: 'text',
      transformOrigin: '0% 0 %',
    },
  },
  //
  // animation keyframes
  //
  '@keyframes page-flip-out-right': {
    '100%': { transform: 'rotateY(-90deg) translateX(-2px) skewY(-15deg)' },
  },
  '@keyframes page-flip-in-left': {
    '0%': { transform: 'rotateY(90deg) translateX(-2px) skewY(15deg)' },
    '50%': { transform: 'rotateY(90deg) translateX(-2px) skewY(15deg)' },
    '100%': { transform: 'rotateY(0) translateX(0) skewY(0deg)' },
  },
  '@keyframes page-flip-out-left': {
    '100%': { transform: 'rotateY(90deg) translateX(2px) skewY(15deg)' },
  },
  '@keyframes page-flip-in-right': {
    '0%': { transform: 'rotateY(-90deg) translateX(2px) skewY(-15deg)' },
    '50%': { transform: 'rotateY(-90deg) translateX(2px) skewY(-15deg)' },
    '100%': { transform: 'rotateY(0) translateX(0) skewY(0deg)' },
  },

  '@keyframes page-flip-out': {
    '100%': { transform: 'rotateX(-90deg) translateY(-2px) skewX(-5deg)' },
  },
  '@keyframes page-flip-in': {
    '0%': { transform: 'rotateX(-90deg) translateY(-2px) skewX(-5deg)' },
    '100%': { transform: 'rotateX(0deg) translateY(0) skewX(0deg)' },
  },
}))

// Constants
const START_PAGE_INDEX = 0
const SCALE_MODE_PAGE = 'page'
const SCALE_MODE_WIDTH = 'width'
const SCALE_MODE_REAL = 'real'
const SCALE_OPTIONS = [
  SCALE_MODE_REAL,
  SCALE_MODE_WIDTH,
  SCALE_MODE_PAGE,
  0.5,
  0.75,
  1.0,
  1.25,
  1.5,
  2.0,
]

export const MODE_SEQUENTIAL = 0
export const MODE_ONE_BY_ONE = 1
export const MODE_TWO_IN_ONE = 2
export const MODES = [
  MODE_SEQUENTIAL,
  MODE_ONE_BY_ONE,
  MODE_TWO_IN_ONE,
]

const DIRECTION_LEFT_TO_RIGHT = 0
const DIRECTION_RIGHT_TO_LEFT = 1
const DIRECTIONS = [
  DIRECTION_LEFT_TO_RIGHT,
  DIRECTION_RIGHT_TO_LEFT,
]

const buildDraggableKey = () => {
  return String(new Date().getTime())
}

const isOddNumber = (value) => {
  if (typeof value !== 'number') return false
  return value % 2 === 1
}
const isEvenNumber = (value) => {
  if (typeof value !== 'number') return false
  return value % 2 === 0
}

const _download = (blobUrl, filename) => {
  var a = document.createElement('a')

  if (!a.click) {
    throw new Error('"a.click()" is not supported.')
  }

  a.href = blobUrl
  a.target = '_parent'

  if ('download' in a) {
    a.download = filename
  }

  (document.body || document.documentElement).appendChild(a)
  a.click()
  a.remove()
}

const _downloadFile = (blob, filename) => {
  if (navigator.msSaveBlob && navigator.msSaveBlob(blob, filename)) {
    return
  }

  const blobUrl = pdfjsLib.URL.createObjectURL(blob)
  _download(blobUrl, filename)
}

const _pageCanvasName = (index) => {
  return `page-canvas-${index}`
}
const _pageTextLayerName = (index) => {
  return `page-text-${index}`
}

const shrinkSizeInPixels = (maxPixels, currentWidth, currentHeight) => {
  const currentPixels = currentWidth * currentHeight;
  if (currentPixels <= maxPixels) {
    return [currentWidth, currentHeight]; // 既に上限以下ならそのまま返す
  }
  const ratio = Math.sqrt(maxPixels / currentPixels); // アスペクト比を維持するための縮小率を求める
  const newWidth = Math.floor(currentWidth * ratio);
  const newHeight = Math.floor(currentHeight * ratio);
  return [newWidth, newHeight];
}

//
// Viewer
//
const Viewer = (props) => {
  const { cover, direction, download, logo, mode: originalMode, name, pdf } = props
  // states
  const [mode, setMode] = React.useState(originalMode)
  const [currentPage, setCurrentPage] = React.useState(START_PAGE_INDEX)
  const [prevPage, setPrevPage] = React.useState(START_PAGE_INDEX)
  const [scaleMode, setScaleMode] = React.useState(SCALE_MODE_PAGE)
  const [draggableKey, setDraggableKey] = React.useState(buildDraggableKey())
  const [dragged, setDragged] = React.useState(false)
  // refs
  const rootRef = React.useRef(null)
  const headerRef = React.useRef(null)
  const bodyRef = React.useRef(null)
  const footerRef = React.useRef(null)
  // as instance variables
  const scaleValue = React.useRef(null)
  const needRerendering = React.useRef(true)
  // const offset = React.useRef({ x: 0, y: 0 })
  const pinchTimerIdRef = React.useRef(null)
  const pinchBaseDistanceRef = React.useRef(0)
  const pinchBaseScaleRef = React.useRef(0)
  const immediateRenderingTimerId = React.useRef(null)
  const resizeOnlyOnRendering = React.useRef(false)
  //
  const classes = useStyles()
  const isDesktopSize = useMediaQuery((theme) =>
    theme.breakpoints.up('md')
  )
  const iconButtonCommonProps = {
    size: isDesktopSize ? "large" : "small",
  }
  const iconCommonProps = {
    fontSize: isDesktopSize ? undefined : "small",
  }
  const swipeHandlers = useSwipeable({
    onSwiped: (evt) => {
      if (evt.dir === 'Left' || evt.dir === 'Up') {
        // 次のページに移動
        toRight()
      }
      if (evt.dir === 'Right' || evt.dir === 'Down') {
        // 前のページに移動
        toLeft()
      }
    },
    delta: 20,
    trackTouch: true, // タッチ操作に対応
    trackMouse: false, // マウス操作は非対応
    swipeDuration: 1000, // 1秒以内操作を許容
  })
  const forceUpdate = useForceUpdateComponent()
  //

  //
  // effects
  //
  React.useEffect(() => {
    renderAllPage(true)
  }, []) // mounted/unmounted

  React.useEffect(() => {
    if (needRerendering.current) {
      renderAllPage(resizeOnlyOnRendering.current)
    }
    needRerendering.current = true // init
  }) // always

  //
  // functions
  //
  const scrollTo = (index) => {
    if (mode !== MODE_SEQUENTIAL) return

    const canvas = document.querySelector(`#${_pageCanvasName(index)}`)
    if (!canvas) return

    canvas.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // canvas.scrollIntoView()
  }

  const baseIndexFor = (pageIndex) => {
    if (mode != MODE_TWO_IN_ONE) return pageIndex

    let baseIndex = pageIndex
    if (cover) {
      if (baseIndex > 0 && isEvenNumber(baseIndex)) {
        baseIndex--
      }
    } else {
      if (isOddNumber(baseIndex)) {
        baseIndex--
      }
    }
    return baseIndex
  }
  const isCurrent = (pageIndex) => {
    if (mode == MODE_SEQUENTIAL) return true
    if (pageIndex == currentPage) return true
    if (mode == MODE_TWO_IN_ONE) {
      const baseIndex = baseIndexFor(pageIndex)
      if (baseIndex == currentPage) {
        return true
      }
    }

    return false
  }
  const isPrev = (pageIndex) => {
    if (mode == MODE_SEQUENTIAL) return false

    if (mode == MODE_ONE_BY_ONE) {
      if (pageIndex == prevPage) return true
    } else if (mode == MODE_TWO_IN_ONE) {
      const baseIndex = baseIndexFor(pageIndex)
      //const prevBaseIndex = baseIndexFor(pageIndex - 2)

      // console.debug('prev:', prevPage, ', index:', pageIndex, ', base:', baseIndex)
      if (baseIndex == prevPage) {
        return true
      }
    }

    return false
  }

  const updateCurrentPage = (value) => {
    if (currentPage == value) return

    setPrevPage(currentPage)
    setCurrentPage(value)
    needRerendering.current = false
  }

  const showNextPage = () => {
    var offset = 1
    var page = currentPage
    if (mode == MODE_TWO_IN_ONE) {
      offset = 2
      if (cover && page == 0) {
        page = -1
      }
    }

    var newPage = page + offset
    if (newPage >= pdf.numPages) return

    updateCurrentPage(newPage)
  }
  const showPrevPage = () => {
    if (currentPage < 0) null

    var offset = 1
    if (mode == MODE_TWO_IN_ONE) offset = 2

    var newPage = currentPage - offset
    if (newPage < 0) newPage = 0 //return

    updateCurrentPage(newPage)
  }
  const showLastPage = () => {
    var newPage = pdf.numPages - 1
    if (mode == MODE_TWO_IN_ONE) {
      if (cover) {
        if (isEvenNumber(newPage))
          newPage--
      } else {
        if (isOddNumber(newPage))
          newPage--
      }
    }
    updateCurrentPage(newPage)
  }
  const showFirstPage= () => {
    updateCurrentPage(0)
  }
  const toLeft = () => {
    if (direction == DIRECTION_RIGHT_TO_LEFT) {
      showNextPage()
    } else {
      showPrevPage()
    }
  }
  const toRight = () => {
    if (direction == DIRECTION_RIGHT_TO_LEFT) {
      showPrevPage()
    } else {
      showNextPage()
    }
  }
  const toLeftEnd = () => {
    if (direction == DIRECTION_RIGHT_TO_LEFT) {
      showLastPage()
    } else {
      showFirstPage()
    }
  }
  const toRightEnd = () => {
    if (direction == DIRECTION_RIGHT_TO_LEFT) {
      showFirstPage()
    } else {
      showLastPage()
    }
  }
  const handleLayoutModeOnChange = (value) => {
    setMode(value)
  }
  // for scaling
  const zoomIn = () => {
    var theScaleMode = scaleMode
    if (typeof scaleMode == 'string') {
      theScaleMode = scaleValue.current + 0.1
    } else {
      theScaleMode = scaleMode + 0.1
    }
    if (theScaleMode > 2.0) theScaleMode = 2.0

    setScaleMode(theScaleMode)
  }
  const zoomOut = () => {
    var theScaleMode = scaleMode
    if (typeof scaleMode == 'string') {
      theScaleMode = scaleValue.current - 0.1
    } else {
      theScaleMode = scaleMode - 0.1
    }
    if (theScaleMode < 0.1) theScaleMode = 0.1

    setScaleMode(theScaleMode)
  }
  const zoomUpdate = (scale) => {
    let theScale = scale
    if (theScale < 0.1) theScale = 0.1
    if (theScale > 2.0) theScale = 2.0

    // ピンチ操作により短時間に更新処理が行われる可能性が高いので
    // このメソッドが利用されたときは、canvasのリサイズのみにし
    // 1秒後にフラグをリセット。ただし、連続して呼び出されたときは直前の
    // リセットがキャンセルされるのでフラグが継続される。
    resizeOnlyOnRendering.current = true
    clearTimeout(immediateRenderingTimerId.current)
    immediateRenderingTimerId.current = setTimeout(() => {
      resizeOnlyOnRendering.current = false
      // フラグをリセットしたので実際にレンダリングさせるために
      // コンポーネントを強制的に更新。
      forceUpdate()
    }, 1000)

    setScaleMode(theScale)
  }
  // const zoomReset = () => {
  //   setPageScale(SCALE_INIT)
  // }
  const handleZoomOnPinch = (evt) => {
    evt.preventDefault()

    const touches = evt.changedTouches

    if (touches.length !== 2) return // nop

    const pt0 = { x: touches[0].pageX, y: touches[0].pageY }
    const pt1 = { x: touches[1].pageX, y: touches[1].pageY }

    const distance = Math.hypot(pt1.x - pt0.x, pt1.y - pt0.y)

    clearTimeout(pinchTimerIdRef.current)

    if (pinchBaseDistanceRef.current && pinchBaseScaleRef.current) {
      const pinchScale = distance / pinchBaseDistanceRef.current
      // console.debug("pinch scale: " + pinchScale)
      if (pinchScale === 0) {
        // nop
      } else {
        const newScale = pinchBaseScaleRef.current * pinchScale
        // console.debug("new scale: " + newScale)
        zoomUpdate(newScale)
      }

      pinchTimerIdRef.current = setTimeout(() => {
        pinchBaseDistanceRef.current = 0
      }, 100)
    } else {
      pinchBaseDistanceRef.current = distance
      if (typeof scaleMode === 'string') {
        pinchBaseScaleRef.current = scaleValue.current
      } else {
        pinchBaseScaleRef.current = scaleMode
      }
    }
  }
  // for react-draggable
  const handleOnDragStart = (evt, data) => {
    const touches = evt.changedTouches ? evt.changedTouches : []
    // console.debug("drag start event type: ", evt.type, ', touches: ', touches.length)

    if (evt.type === 'mousedown') {
      // ok
    } else if (evt.type === 'touchstart' && touches.length === 2) {
      const pt0 = { x: touches[0].pageX, y: touches[0].pageY }
      const pt1 = { x: touches[1].pageX, y: touches[1].pageY }

      const distance = Math.hypot(pt1.x - pt0.x, pt1.y - pt0.y)
      console.debug("finger distance: " + distance)
      // 指が離れすぎている場合はピンチ操作の可能性ありとしドラッグ操作にしない
      if (distance > 100) return false
    } else {
      // drag disabled
      return false
    }
  }
  const handleDragging = (evt, data) => {
    evt.stopPropagation()
    const theDragged = data.x != 0 || data.y != 0
    if (dragged == theDragged) return

    setDragged(theDragged)
  }
  const handleOnDragStop = (evt, data) => {

  }
  const resetDraggable = () => {
    setDraggableKey(buildDraggableKey())
    setDragged(false)
    setPrevPage(currentPage)
  }
  //
  const handleScaleModeChange = (event, child) => {
    scaleValue.current = null
    setScaleMode(event.target.value)
  }
  //
  const handleScrollspy = (node) => {
    // console.debug(node)
    const index = Number(node.getAttribute('data-index'))

    if (isNaN(index)) return

    updateCurrentPage(index)
  }
  //
  const handleResize = () => {
    renderAllPage()
  }
  //
  const handleFileDownload = () => {
    if (!pdf || !download) return

    pdf.getData().then((data) => {
      var blob = new Blob([data], {
        type: 'application/pdf',
      })

      let filename = name
      if (!filename.toLowerCase().endsWith('.pdf')) {
        filename += '.pdf'
      }
      _downloadFile(blob, filename)
    })
  }
  //

  //
  // render methods
  //
  // PDF rendering
  const renderPage = (pdf, i, renderNext = false, resizeOnly = false) => {
    if (!needRerendering.current) return

    const bodyNode = bodyRef.current

    const getAdjustedSize = (width, height) => {
      const userAgent = navigator.userAgent;

      if (/iPhone|iPod|iPad|Android/i.test(userAgent)) {
        // for スマートフォン
        // 縦*横 が16777216ピクセスに収まる様にサイズ調整
        // iPhoneで上限を超えないようにする処理。
        // 上限の値は機種によって違うかも。
        return  shrinkSizeInPixels(16777216, width, height)
      } else {
        return [width, height] // そのまま返す
      }
    }
    const handlePage = (page, index) => {
      // 読み込んだ各ページの処理を記述
      const canvas = document.querySelector(`#${_pageCanvasName(index)}`)

      if (!canvas) {
        return
      }

      // 拡大率を計算
      const pageSize = {
        width: page.view[2] * (mode == MODE_TWO_IN_ONE ? 2 : 1),
        height: page.view[3],
      }
      const bodySize = {
        width: bodyNode.clientWidth - 60,
        height: bodyNode.clientHeight - 30,
      }
      let scale = 1.0
      switch (scaleMode) {
        case SCALE_MODE_PAGE:
          const scaleByWidth = bodySize.width / pageSize.width
          const scaleByHeight = bodySize.height / pageSize.height
          if (scaleByWidth > scaleByHeight) {
            scale = scaleByHeight
          } else {
            scale = scaleByWidth
          }
          break
        case SCALE_MODE_WIDTH:
          scale = bodySize.width / pageSize.width
          break
        case SCALE_MODE_REAL:
          scale = 1.0
          break
        default:
          scale = scaleMode
      }
      scaleValue.current = scale
      // console.debug('update scale value:', scaleValue.current)

      // 描画時の設定 (高密度(Retina)ディスプレイに対応)
      const viewport = page.getViewport({ scale: scale * window.devicePixelRatio, rotate: 0 })
      const [ w, h ] = getAdjustedSize(viewport.width, viewport.height)
      canvas.width = w //viewport.width
      canvas.height = h //viewport.height
      canvas.style.width = String(canvas.width / window.devicePixelRatio) + "px"
      canvas.style.height = String(canvas.height / window.devicePixelRatio) + "px"

      if (resizeOnly) {
        // 次のページを描画
        if (renderNext) {
          const nextIndex = index + 1
          if (nextIndex < pdf.numPages) {
            renderPage(pdf, nextIndex, renderNext, resizeOnly)
          }
        }
      } else {
        // 作成した canvas に読み込んだ pdf を描画
        const ctx = canvas.getContext('2d')
        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        }
        page.render(renderContext).promise.then(() => {
          if (renderNext) {
            const nextIndex = index + 1
            if (nextIndex < pdf.numPages) {
              renderPage(pdf, nextIndex, renderNext, resizeOnly)
            }
          }

          // render text layer
          //return page.getTextContent()
        })
        // .then((textContent) => {
        //   const textLayer = document.querySelector(`#${_pageTextLayerName(index)}`)

        //   // textLayer.width = canvas.width
        //   // textLayer.height = canvas.height
        //   // textLayer.style.width = canvas.style.width
        //   // textLayer.style.height = canvas.style.height
        //   // const canvasRect = canvas.getBoundingClientRect()
        //   // // top
        //   // var scrollTop = window.pageYOffset || document.documentElement.scrollTop
        //   // textLayer.style.top = canvasRect.top + scrollTop
        //   // // left
        //   // var scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
        //   // textLayer.style.left = canvasRect.left + scrollLeft

        //   // Pass the data to the method for rendering of text over the pdf canvas.
        //   var textLayerViewport = page.getViewport({ scale: scale, rotate: 0 })
        //   pdfjsLib.renderTextLayer({
        //     textContent: textContent,
        //     container: textLayer,
        //     viewport: textLayerViewport,
        //     textDivs: []
        //   })
        // })
      }
    }
    const handleError = (error, index) => {
      console.error(error)

      const canvas = document.querySelector(`#${_pageCanvasName(index)}`)
      if (!canvas) return

      canvas.style.display = 'none'
    }

    const pageIdx = i == null ? currentPage : i
    pdf.getPage(pageIdx + 1).then(
      (page) => handlePage(page, pageIdx),
      (error) => handleError(error, pageIdx)
    )
  }
  const renderAllPage = (resizeOnly = false) => {
    // console.debug('render all page')
    renderPage(pdf, 0, true, resizeOnly)
  }

  const renderHeader = () => {
    const pdfDoc = pdf

    return (
      <AppBar
        className={classes.header}
        position="static"
        elevation={1}
        ref={headerRef}
      >
        <Toolbar className={classes.headerBar} variant="dense">
          {logo ?
            <img className={classes.logo} src={logo}  />
            : null}
          <Hidden smDown>
            <Typography
              variant="h6"
              color="inherit"
              className={classes.title}
            >
              {name && name.length > 0 ? name : 'PDF Viewer'}
            </Typography>
          </Hidden>
          <div className={classes.grow} />
          <FormControl className={classes.formControl} size={isDesktopSize ? undefined : 'small'}>
            {/* <InputLabel id="demo-simple-select-outlined-label">
                Age
              </InputLabel> */}
            <Select
              labelId="demo-simple-select-outlined-label"
              id="demo-simple-select-outlined"
              value={scaleMode}
              onChange={(event, child) => handleScaleModeChange(event, child)}
              //labelWidth={labelWidth}
              classes={{ root: classes.scaleSelectRoot }}
              MenuProps={{ classes: { list: classes.selectMenu } }}
            >
              {scaleMode && SCALE_OPTIONS.indexOf(scaleMode) < 0 ?
                <MenuItem key={-1} value={scaleMode}>{`${Math.round(scaleMode * 100)}%`}</MenuItem> :
                null}
              {SCALE_OPTIONS.map((opt, i) => {
                var label = null
                switch (opt) {
                  case SCALE_MODE_REAL:
                    label = i18n.t('page_scale_actual')
                    break
                  case SCALE_MODE_WIDTH:
                    label = i18n.t('page_scale_width')
                    break
                  case SCALE_MODE_PAGE:
                    label = i18n.t('page_scale_fit')
                    break
                  default:
                    label = `${Math.round(opt * 100)}%`
                }
                return <MenuItem key={i} value={opt}>{label}</MenuItem>
              })}
            </Select>
          </FormControl>
          <div className={classes.navigations}>
            <IconButton color="inherit" onClick={() => zoomIn()} {...iconButtonCommonProps}
>
              <ZoomInIcon />
            </IconButton>
            <IconButton color="inherit" onClick={() => zoomOut()} {...iconButtonCommonProps}>
              <ZoomOutIcon />
            </IconButton>
            {mode === MODE_SEQUENTIAL ? null :
              <IconButton
                color="inherit"
                disabled={!dragged}
                onClick={() => resetDraggable()}
                {...iconButtonCommonProps}
              >
                <CenterIcon />
              </IconButton>
            }
            {!download ? null :
              <IconButton color="inherit" onClick={() => handleFileDownload()} {...iconButtonCommonProps}>
                <DownloadIcon />
              </IconButton>
            }
          </div>
        </Toolbar>
      </AppBar>
    )
  }

  const renderFooter = () => {
    const pdfDoc = pdf

    const pageSliderLabelFormat = (value) => {
      const val = Math.abs(value)
      var label = '' + (val + 1)
      if (mode == MODE_TWO_IN_ONE) {
        if (!(cover && val === 0) && val + 1 < pdfDoc.numPages) {
          label += `-${val + 2}`
        }
      }
      return label
    }
    const handlePageSlider = (value) => {
      const val = Math.abs(value)
      updateCurrentPage(val)
      scrollTo(val)
    }

    // if (mode == MODE_SEQUENTIAL) return null

    var sliderValue = currentPage
    var sliderMarks = true
    var sliderStep = 1
    var sliderMin = 0
    var sliderMax = pdfDoc.numPages - 1
    if (mode == MODE_TWO_IN_ONE) {
      sliderStep = null // marksに従ったステップになるので null で処理
      sliderMarks = []
      var markIdxInit = 0
      if (cover) {
        markIdxInit = 1
        sliderMarks.push({
          value: 0,
        })
      }
      for (var i = markIdxInit; i < pdfDoc.numPages; i += 2) {
        var idx = i
        if (direction == DIRECTION_RIGHT_TO_LEFT) {
          sliderMarks.unshift({
            value: -idx,
            //label: `${i+1}-${i+2}`,
          })
        } else {
          sliderMarks.push({
            value: idx,
            //label: `${i+1}-${i+2}`,
          })
        }
      }

      if (direction == DIRECTION_RIGHT_TO_LEFT) {
        sliderValue *= -1
      }
      sliderMin = sliderMarks[0].value
      sliderMax = sliderMarks[sliderMarks.length - 1].value
    }

    return (
      <React.Fragment>
        <AppBar
          className={classes.footer}
          component="footer"
          position="static"
          color="default"
          elevation={1}
          ref={footerRef}
        >
          <Toolbar className={classes.footerBar} variant="dense">
            <div className={classes.footerBarLeft}>
              <FullscreenButton target={rootRef.current} buttonProps={iconButtonCommonProps} iconProps={iconCommonProps} />
            </div>
            <div className={classes.footerBarCenter}>
              <div className={classes.navigations}>
                {mode == MODE_SEQUENTIAL ? null : (
                  <React.Fragment>
                    <IconButton color="inherit" onClick={() => toLeftEnd()} {...iconButtonCommonProps}>
                      <FirstPageIcon {...iconCommonProps} />
                    </IconButton>
                    <IconButton color="inherit" onClick={() => toLeft()} {...iconButtonCommonProps}>
                      <ChevronLeftIcon {...iconCommonProps} />
                    </IconButton>
                  </React.Fragment>
                )}
                <Slider
                  className={classes.pageSlider}
                  size={isDesktopSize ? undefined : 'small'}
                  marks={sliderMarks}
                  valueLabelDisplay="on"
                  valueLabelFormat={pageSliderLabelFormat}
                  defaultValue={START_PAGE_INDEX}
                  value={sliderValue}
                  min={sliderMin}
                  max={sliderMax}
                  step={sliderStep}
                  track={false}
                  onChange={(e, v) => handlePageSlider(v)}
                />
                {mode == MODE_SEQUENTIAL ? null : (
                  <React.Fragment>
                    <IconButton color="inherit" onClick={() => toRight()} {...iconButtonCommonProps}>
                      <ChevronRightIcon {...iconCommonProps} />
                    </IconButton>
                    <IconButton color="inherit" onClick={() => toRightEnd()} {...iconButtonCommonProps}>
                      <LastPageIcon {...iconCommonProps} />
                    </IconButton>
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className={classes.footerBarRight}>
              <LayoutModeButton
                mode={mode}
                onChange={handleLayoutModeOnChange}
                buttonProps={iconButtonCommonProps}
                iconProps={iconCommonProps}
              />
            </div>
          </Toolbar>
        </AppBar>
      </React.Fragment>
    )
  }

  const renderCanvases = () => {
    const rtl = direction == DIRECTION_RIGHT_TO_LEFT

    const isLeftPage = (idx) => {
      if (mode !== MODE_TWO_IN_ONE) return false

      let checker = null
      if (cover) {
        checker = rtl ? isEvenNumber : isOddNumber
      } else {
        checker = rtl ? isOddNumber : isEvenNumber
      }

      return checker(idx)
    }
    const isRightPage = (idx) => {
      if (mode !== MODE_TWO_IN_ONE) return false

      let checker = null
      if (cover) {
        checker = rtl ? isEvenNumber : isOddNumber
      } else {
        checker = rtl ? isOddNumber : isEvenNumber
      }

      return !checker(idx)
    }


    // prepare canvas nodes
    const canvasCount = pdf.numPages
    var nodes = []
    var canvasIds = []
    var textLayerIds = []

    // console.groupCollapsed('canvas state')
    for (var i = 0; i < canvasCount; i++) {
      var klasses = []
      var display = 'none'

      switch (mode) {
        case MODE_ONE_BY_ONE:
          klasses.push(classes.canvasAbsWrapper)
          if (isPrev(i)) {
            display = 'block'
            if (prevPage < currentPage) {
              klasses.push('out')
            }
          } else if (isCurrent(i)) {
            display = 'block'
            if (prevPage > currentPage) {
              klasses.push('in')
            }
          }
          break
        case MODE_TWO_IN_ONE:
          klasses.push(classes.canvasAbsWrapper)
          if (isLeftPage(i)) {
            klasses.push(classes.canvasLeft)
          } else if (isRightPage(i)) {
            klasses.push(classes.canvasRight)
          }

          if (isPrev(i)) {
            display = 'block'
            if (prevPage < currentPage) {
              if (rtl ? isLeftPage(i) : isRightPage(i)) {
                klasses.push('out')
              }
            } else if (prevPage > currentPage) {
              if (rtl ? isRightPage(i) : isLeftPage(i)) {
                klasses.push('out')
              }
            }
          } else if (isCurrent(i)) {
            display ='block'
            if (prevPage < currentPage) {
              if (rtl ? isRightPage(i) : isLeftPage(i)) {
                klasses.push('in')
              }
            } else if (prevPage > currentPage) {
              if (rtl ? isLeftPage(i) : isRightPage(i)) {
                klasses.push('in')
              }
            }
          }
          break
        case MODE_SEQUENTIAL:
        default:
          display = 'block'
      }

      // console.debug('[', i, '] display:', display)

      const canvasId = _pageCanvasName(i)
      const textLayerId = _pageTextLayerName(i)
      canvasIds.push(canvasId)
      textLayerIds.push(textLayerId)

      nodes.push(
        <div
          key={`canvas-container-${i}`}
          className={classnames(...klasses)}
          style={{ display: display }}
        >
          <canvas
            id={canvasId}
            className={classnames(classes.canvas)}
            data-index={i}
          />
          <div
            id={textLayerId}
            className={classnames(classes.textLayer)}
          />
        </div>
      )
    }

    // console.groupEnd()

    var containerClasses = [classes.canvasContainer]
    if (mode === MODE_SEQUENTIAL) {
      containerClasses.push(classes.canvasContainerSeq)
    }

    var canvases = <div className={classnames(...containerClasses)}>{nodes}</div>
    if (mode === MODE_SEQUENTIAL) {
      canvases = (
        <>
          <Scrollspy
            componentTag="div"
            items={canvasIds}
            rootEl={`.${classes.body}`}
            offset={-20}
            onUpdate={(node) => handleScrollspy(node)}
          />
          {canvases}
        </>
      )
    } else {
      canvases = (
        <Box onTouchMove={handleZoomOnPinch}>
          <Box {...swipeHandlers}>
            <Draggable
              key={draggableKey} // When a key is changed, Draggable is renewed. (to reset position)
              // defaultPosition={ {x: 0, y: 0} }
              // position={null}
              onStart={handleOnDragStart}
              onDrag={handleDragging}
              onStop={handleOnDragStop}
            >
              {canvases}
            </Draggable>
          </Box>
        </Box>
      )
    }

    return canvases
  }

  //
  // main
  //
  const pdfDoc = pdf

  return (
    <div ref={rootRef} className={classnames(classes.root, (download ? 'printable' : null))}>
      <EventListener target="window" onResize={() => handleResize()} />
      <CssBaseline />
      {renderHeader()}

      <div className={classnames(classes.body, (mode !== MODE_SEQUENTIAL ? classes.bodyNoScroll : null))} ref={bodyRef}>
        {/* <Container> */}
          <div id="back-to-top-anchor" />
          {/* <Box> */}
            {renderCanvases()}
          {/* </Box> */}
          {mode == MODE_SEQUENTIAL && bodyRef.current ?
            <ScrollTop
              {...props}
              anchor="#back-to-top-anchor"
              window={() => bodyRef.current}
            >
              <Fab color="secondary" size="small" aria-label="scroll back to top">
                <KeyboardArrowUpIcon />
              </Fab>
            </ScrollTop> : null}
        {/* </Container> */}
      </div>

      {renderFooter()}
    </div>
  )
}

Viewer.propTypes = {
  name: PropTypes.string,
  mode: PropTypes.oneOf(MODES),
  direction: PropTypes.oneOf(DIRECTIONS),
  cover: PropTypes.bool,
  download: PropTypes.bool,
}
Viewer.defaultProps = {
  name: null,
  mode: MODE_ONE_BY_ONE,
  direction: DIRECTION_LEFT_TO_RIGHT,
  cover: false,
  download: false,
}

export default Viewer
